'use client';

import { useState, useEffect } from 'react';
import { signIn } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/IconButton';

interface AuthFormProps {
  callbackUrl?: string;
  verified?: boolean;
  mode?: 'signin' | 'signup';
}

type AuthStep = 'email' | 'verify-email' | 'password' | 'signin' | 'setup-password-sent';

export default function AuthForm({ callbackUrl = '/home', verified = false, mode = 'signup' }: AuthFormProps) {
  const router = useRouter();
  // Set initial step based on whether we're in signin or signup mode
  const [step, setStep] = useState<AuthStep>(mode === 'signin' ? 'signin' : 'email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExistingUser, setIsExistingUser] = useState(mode === 'signin');
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [showSignInPassword, setShowSignInPassword] = useState(false);

  // Make sure step is set correctly when mode changes
  useEffect(() => {
    if (mode === 'signin') {
      setStep('signin');
      setIsExistingUser(true);
    } else {
      setStep('email');
      setIsExistingUser(false);
    }
  }, [mode]);

  // Check if email exists when user enters email
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);

    try {
      // If we're already in signin mode, skip email check and go straight to signin form
      if (mode === 'signin') {
        setStep('signin');
        setIsLoading(false);
        return;
      }
      
      // For signup mode, check if user exists
      const response = await fetch('/api/auth/check-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();
      console.log(`Email check response for ${email}:`, data);

      if (response.ok) {
        if (data.exists) {
          console.log(`User exists, switching to signin mode`);
          setIsExistingUser(true);
          // If user exists but has no password, offer to set one
          if (!data.hasPassword) {
            setStep('password'); // Let them set a password
            toast.warning('Email already registered', { description: 'Please set a password to complete your account setup.' });
          } else {
            setStep('signin');
          }
        } else {
          console.log(`New user, switching to password/registration mode`);
          setIsExistingUser(false);
          setStep('password');
        }
      } else {
        toast.error('Something went wrong', { description: data.error });
      }
    } catch (error) {
      toast.error('Network error', { description: 'Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle user registration
  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsLoading(true);

    try {
      // If this is an existing Google user setting a password: request an
      // emailed, token-gated link (the password itself is set via that link
      // on /auth/reset-password, not submitted directly here) -- proves the
      // caller owns the mailbox before any password can be changed.
      if (isExistingUser) {
        if (!email) return;

        const response = await fetch('/api/auth/set-password', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email })
        });

        const data = await response.json();

        if (response.ok) {
          setStep('setup-password-sent');
        } else {
          toast.error('Something went wrong', { description: data.error });
        }
      } else {
        // Regular new user registration
        if (!email || !password || !name) return;

        const response = await fetch('/api/auth/register', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password, name })
        });

        const data = await response.json();

        if (response.ok) {
          setStep('verify-email');
        } else {
          toast.error('Registration failed', { description: data.error });
        }
      }
    } catch (error) {
      toast.error('Network error', { description: 'Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle user sign in
  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;

    setIsLoading(true);

    try {
      // Only check if user exists if we haven't already confirmed
      if (!isExistingUser) {
        // Check if user exists first
        const response = await fetch('/api/auth/check-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email })
        });

        const data = await response.json();

        if (response.ok && !data.exists) {
          toast.warning('No account found', { description: 'No account found with this email.' });
          setIsLoading(false);
          return;
        }
      }

      const result = await signIn('credentials', {
        email,
        password,
        redirect: false,
      });

      if (result?.error) {
        // Check if user needs to set up password (previously Google user)
        if (result.error === 'SETUP_PASSWORD_REQUIRED') {
          setStep('password');
          toast.info('Password setup required', { description: 'This account was created with Google Sign-in. Please set up a password to continue.' });
          setIsExistingUser(true); // Show "Set Password" form, not new registration form
        } else {
          toast.error('Sign in failed', { description: result.error });
        }
      } else if (result?.ok) {
        // Fetch updated session to check onboarding status
        const updatedSession = await fetch('/api/auth/session').then(res => res.json());
        
        // Check if user needs to complete onboarding
        if (updatedSession?.user && !updatedSession.user.onboardingComplete) {
          console.log('🔄 Redirecting to onboarding - user has not completed onboarding');
          router.push('/onboarding');
        } else {
          router.push(callbackUrl);
        }
      }
    } catch (error) {
      toast.error('Sign in failed', { description: 'Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setStep('email');
    setEmail('');
    setPassword('');
    setName('');
    setIsExistingUser(false);
  };

  return (
    <div className="max-w-md mx-auto bg-card p-8 rounded-lg shadow-md">
      {/* Header */}
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold text-foreground">
          {step === 'verify-email' || step === 'setup-password-sent' ? 'Check Your Email' :
           step === 'signin' ? 'Welcome Back' :
           step === 'password' ? 'Create Account' : 'Get Started'}
        </h1>
        <p className="text-muted-foreground mt-2">
          {step === 'verify-email' ? 'We sent you a verification link' :
           step === 'setup-password-sent' ? 'We sent you a link to set a password' :
           step === 'signin' ? 'Sign in to your account' :
           step === 'password' ? 'Complete your registration' : 'Enter your work email to continue'}
        </p>
      </div>

      {/* Success message for verified email */}
      {verified && (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
          ✅ Email verified successfully! You can now sign in.
        </div>
      )}

      {/* Email Step */}
      {step === 'email' && (
        <form onSubmit={handleEmailSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-foreground">
              Work Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="mt-1 block w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground mt-1">
              We only accept work emails (no Gmail, Yahoo, etc.)
            </p>
          </div>
          <Button
            type="submit"
            disabled={isLoading || !email}
            isLoading={isLoading}
            className="w-full bg-primary-600 text-white hover:bg-primary-700"
          >
            {isLoading ? 'Checking...' : 'Continue'}
          </Button>
        </form>
      )}

      {/* Registration Step */}
      {step === 'password' && !isExistingUser && (
        <form onSubmit={handleSignUp} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-foreground">
              Full Name
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Doe"
              className="mt-1 block w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
              disabled={isLoading}
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-foreground">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showRegPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                className="mt-1 block w-full px-3 py-2 pr-10 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
                minLength={8}
                disabled={isLoading}
              />
              <IconButton
                type="button"
                onClick={() => setShowRegPassword(!showRegPassword)}
                icon={showRegPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                label={showRegPassword ? 'Hide password' : 'Show password'}
                variant="ghost"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground hover:bg-transparent"
              />
            </div>
          </div>
          <div className="text-sm text-muted-foreground">
            <p>Creating account for: <strong>{email}</strong></p>
            <Button
              type="button"
              variant="link"
              onClick={resetForm}
              className="h-auto p-0 text-primary hover:underline"
            >
              Use different email
            </Button>
          </div>
          <Button
            type="submit"
            disabled={isLoading || !name || !password}
            isLoading={isLoading}
            className="w-full bg-primary-600 text-white hover:bg-primary-700"
          >
            {isLoading ? 'Creating Account...' : 'Create Account'}
          </Button>
        </form>
      )}

      {/* Password Setup for Existing Google Users: request an emailed link
          rather than submitting a password directly, so the password can
          only ever be set after proving ownership of the mailbox. */}
      {step === 'password' && isExistingUser && (
        <form onSubmit={handleSignUp} className="space-y-4">
          <p className="text-sm text-muted-foreground">
            This account was created with Google Sign-in. We'll email a link to{' '}
            <strong>{email}</strong> so you can set a password.
          </p>
          <div className="text-sm text-muted-foreground">
            <Button
              type="button"
              variant="link"
              onClick={resetForm}
              className="h-auto p-0 text-primary hover:underline"
            >
              Use different email
            </Button>
          </div>
          <Button
            type="submit"
            disabled={isLoading}
            isLoading={isLoading}
            className="w-full bg-primary-600 text-white hover:bg-primary-700"
          >
            {isLoading ? 'Sending...' : 'Email Me a Link'}
          </Button>
        </form>
      )}

      {/* Set-password link sent */}
      {step === 'setup-password-sent' && (
        <div className="text-center space-y-4">
          <div className="text-6xl">📧</div>
          <div>
            <p className="text-muted-foreground">
              If an account needs a password, we sent a link to <strong>{email}</strong>.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Click the link in your email to set a password, then return here to sign in.
            </p>
          </div>
          <Button
            onClick={resetForm}
            variant="secondary"
            className="w-full bg-muted text-foreground hover:bg-accent"
          >
            Use Different Email
          </Button>
        </div>
      )}

      {/* Sign In Step */}
      {step === 'signin' && (
        <form onSubmit={handleSignIn} className="space-y-4">
          {/* Always show email field when on signin page */}
          <div>
            <label htmlFor="signin-email" className="block text-sm font-medium text-foreground">
              Email
            </label>
            <input
              id="signin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="mt-1 block w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
              disabled={isLoading}
            />
          </div>
          <div>
            <label htmlFor="signin-password" className="block text-sm font-medium text-foreground">
              Password
            </label>
            <div className="relative">
              <input
                id="signin-password"
                type={showSignInPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="mt-1 block w-full px-3 py-2 pr-10 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
                disabled={isLoading}
              />
              <IconButton
                type="button"
                onClick={() => setShowSignInPassword(!showSignInPassword)}
                icon={showSignInPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                label={showSignInPassword ? 'Hide password' : 'Show password'}
                variant="ghost"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground hover:bg-transparent"
              />
            </div>
          </div>
          {/* Only show this when we have the email but didn't come directly to signin */}
          {isExistingUser && email && mode === 'signup' && (
            <div className="text-sm text-muted-foreground">
              <p>Signing in as: <strong>{email}</strong></p>
              <Button
                type="button"
                variant="link"
                onClick={resetForm}
                className="h-auto p-0 text-primary hover:underline"
              >
                Use different email
              </Button>
            </div>
          )}
          {/* Forgot Password Link */}
          <div className="text-right">
            <a href="/auth/forgot-password" className="text-sm text-primary hover:underline">
              Forgot password?
            </a>
          </div>
          <Button
            type="submit"
            disabled={isLoading || !password || !email}
            isLoading={isLoading}
            className="w-full bg-primary-600 text-white hover:bg-primary-700"
          >
            {isLoading ? 'Signing In...' : 'Sign In'}
          </Button>
        </form>
      )}

      {/* Email Verification Step */}
      {step === 'verify-email' && (
        <div className="text-center space-y-4">
          <div className="text-6xl">📧</div>
          <div>
            <p className="text-muted-foreground">
              We sent a verification link to <strong>{email}</strong>
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Click the link in your email to verify your account, then return here to sign in.
            </p>
          </div>
          <Button
            onClick={resetForm}
            variant="secondary"
            className="w-full bg-muted text-foreground hover:bg-accent"
          >
            Use Different Email
          </Button>
        </div>
      )}


      
      {/* Footer with contact sales message */}
      {(step === 'email' || step === 'password' || step === 'signin') && mode === 'signin' && (
        <div className="mt-4 text-center text-sm">
          <p className="text-muted-foreground">
            Need an account?{' '}
            <span className="text-foreground font-medium">
              Contact our sales team
            </span>
          </p>
        </div>
      )}
    </div>
  );
}